import {useNavigate, useParams} from "react-router-dom";
import {HStack, Input, Spinner, Table, TableContainer, Tbody, Text, useToast, VStack} from "@chakra-ui/react";
import {useQuery} from "@tanstack/react-query";
import {
    addMediaNew,
    addProducerNew,
    deleteMediaNew,
    editProducerNew,
    getProducersNewById
} from "../../../../data/api/Customer";
import {getData} from "../../../../utils/Utils";
import {ProducerModelNew, ProducerModelQueryParamsNew, SetImageModel} from "../../../../data/model/Producer";
import {Resolver, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {producerSchema} from "../../../../data/schema/productSchema";
import {CheckCircleIcon} from "@chakra-ui/icons";
import * as React from "react";
import {ChangeEvent, useEffect, useRef, useState} from "react";
import {scrollbarCss} from "../../../../theme/theme";
import {AddTableRow} from "../../../../components/atoms/AddTableRow/AddTableRow";
import {BaseContentPage} from "../../../../components/molecules/BaseContentPage/BaseContentPage";
import {toBase64} from "../../../signup/pages/SignupPageThree";
import {ImageSelectorComponent} from "../../../../components/molecules/ImageSelectorComponent/ImageSelectorComponent";
import {MediaSelectorComponent} from "../../../../components/molecules/MediaSelectorComponent/MediaSelectorComponent";
import AddressInput from "../../../../components/molecules/MapChoosPoint";

export const EditProducerPage = () => {
    const navigate = useNavigate();
    const toast = useToast();

    const {id} = useParams();

    const {data, refetch} = useQuery({
        queryKey: ["get-producer", id],
        enabled: !!id,
        queryFn: () => getProducersNewById({id: id || ""}),
        refetchOnWindowFocus: "always",
        staleTime: 0
    });

    const producer = getData<ProducerModelNew>(data);

    const methods = useForm<ProducerModelQueryParamsNew>({
        mode: "all",
        resolver: yupResolver(producerSchema) as Resolver<ProducerModelQueryParamsNew>
    });

    const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];

            toBase64(file).then(base64String => {
                const base64 = base64String as string;

                setImages([...(images || []), {
                    imageBase64: base64,
                    imageUrl: URL.createObjectURL(file),
                }]);
            });
        }
    };

    const handleImageButtonClick = () => {
        imageInputRef?.current?.click();
    }

    const {
        register,
        handleSubmit,
        setValue,
        formState: {
            errors,
            isSubmitting
        }
    } = methods;

    const {
        email,
        name,
        description,
        website,
        phone,
        piva,
        referentName,
        referentSurname,
        referentEmail,
        referentPhone,
        images: imagesOld,
        imageSrc,
        address,
        lat: latInput,
        long: longInput
    } = producer || {};

    const [mainImage, setMainImage] = useState<SetImageModel | undefined>();
    const [isDelete, setIsDelete] = useState(false);
    const [images, setImages] = useState<SetImageModel[]>([]);
    const [imageToDelete, setImageToDelete] = useState<string[]>([]);
    const imageInputRef: React.RefObject<HTMLInputElement> = useRef(null);

    const [addressInput, setAddressInput] = useState<string>('');
    const [lat, setLat] = useState<string>(latInput || "");
    const [lng, setLng] = useState<string>(longInput || "");

    useEffect(() => {
        if (latInput) setLat(latInput || "");
        if (longInput) setLng(longInput || "");
    }, [latInput, longInput]);

    useEffect(() => {
        if (lat) setValue("lat", Number(lat));
        if (lng) setValue("long", Number(lng));
    }, [lat, lng]);

    useEffect(() => {
        const mainImage = producer?.images?.find((value) => value.isMain) || {};
        mainImage && setMainImage({imageUrl: mainImage.url, id: mainImage.id, isMain: true});

        const otherMedia = producer?.images?.filter((value) => !value.isMain) || [];
        otherMedia && setImages(otherMedia.map((value) => ({imageUrl: value.url, id: value.id, isMain: false})));
    }, [producer?.images]);

    const onSubmit = async (producer: ProducerModelQueryParamsNew) => {
        const list: SetImageModel[] = [];
        mainImage?.file && list?.push(mainImage);
        images?.forEach(value => list?.push(value));

        if (id) {
            await editProducerNew(id, {
                ...producer
            }).then(async (data) => {
                const producerNew = getData<ProducerModelNew>(data);
                for (const value of imageToDelete) {
                    if (value !== "") await deleteMediaNew(value);
                }

                console.log(list.map(value => value.isMain));
                for (const value of list) {
                    value.file && await addMediaNew({
                        type: "Producer",
                        id: producerNew?.id || "",
                        isMain: value.isMain || false
                    }, value.file);
                }

                refetch().then(() => navigate("/producers"));
            }).catch(reason => {
                toast({
                    title: "Si è verificato un errore",
                    description: reason?.message ?? "-"
                })
            });
        } else {
            await addProducerNew({
                ...producer
            }).then(async (data) => {
                const producerNew = getData<ProducerModelNew>(data);

                for (const value of list) {
                    value.file && await addMediaNew({
                        type: "Producer",
                        id: producerNew?.id || "",
                        isMain: value.isMain || false
                    }, value.file);
                }

                navigate("/producers", {replace: true});
            }).catch(reason => {
                toast({
                    title: "Si è verificato un errore",
                    description: reason?.message ?? "-"
                })
            });
        }
    }

    function getInputRow({title, fieldName, description, defaultValue, hintText}: {
        title: string,
        fieldName: any,
        defaultValue?: string,
        description?: string,
        hintText?: string,
    }) {
        return <AddTableRow name={title}>
            <VStack alignItems={"flex-start"} w={"100%"}>
                <Input
                    inputMode={"text"}
                    colorScheme={"primary"}
                    variant='filled'
                    _placeholder={{fontSize: 14}}
                    defaultValue={defaultValue}
                    disabled={isSubmitting}
                    {...register(fieldName)}
                    placeholder={hintText}/>
                {description && <Text fontSize={"14px"} color={"rgba(0,0,0,0.6)"}>{description}</Text>}
            </VStack>
        </AddTableRow>;
    }

    return <BaseContentPage>
        <VStack id={"container"} w={'100%'} h={'100%'} alignItems={'center'}>
            <HStack cursor={"pointer"} h={"64px"} px={"24px"} borderRadius={"24px"} position={"absolute"}
                    alignItems={"center"} zIndex={5}
                    justifyContent={"center"} bottom={"24px"} right={"48px"}
                    backgroundColor={isSubmitting ? "primary.100" : "secondary.200"}
                    _hover={{backgroundColor: "tertiary.200"}} onClick={handleSubmit(onSubmit)}>
                {isSubmitting ? <Spinner width={"24px"} height={"24px"}/> :
                    <CheckCircleIcon w={"24px"} h={"24px"} color={"primary.400"} alignSelf={"center"}/>}
                <Text fontWeight={"bold"} color={"primary.400"}>Conferma</Text>
            </HStack>

            <Text fontSize={"24px"} justifySelf={"center"} fontWeight={"bold"}
                  ms={"24px"}>{id ? "Modifica produttore" : "Aggiungi produttore"}</Text>

            <TableContainer w={"100%"} pb={"86px"} alignItems={"center"} whiteSpace={"pre-line"} mr={"16px"}
                            overflowY={"scroll"}
                            css={scrollbarCss({marginRight: 48})}>
                <Table variant={"none"} w={"70%"} mx={"auto"}>
                    <Tbody>
                        {getInputRow({
                            title: "Nome",
                            fieldName: "name",
                            defaultValue: name
                        })}
                        {getInputRow({
                            title: "Descrizione",
                            fieldName: "description",
                            defaultValue: description
                        })}
                        {getInputRow({
                            title: "Sito web",
                            fieldName: "website",
                            defaultValue: website
                        })}
                        {getInputRow({
                            title: "Indirizzo",
                            fieldName: "address",
                            defaultValue: address
                        })}
                        {getInputRow({
                            title: "Telefono",
                            fieldName: "phone",
                            defaultValue: phone
                        })}
                        {getInputRow({
                            title: "Partita IVA",
                            fieldName: "piva",
                            defaultValue: piva
                        })}

                        {getInputRow({
                            title: "Email del referente",
                            fieldName: "referentEmail",
                            defaultValue: referentEmail
                        })}
                        {getInputRow({
                            title: "Nome del referente",
                            fieldName: "referentName",
                            defaultValue: referentName
                        })}
                        {getInputRow({
                            title: "Cognome del referente",
                            fieldName: "referentSurname",
                            defaultValue: referentSurname
                        })}
                        {getInputRow({
                            title: "Telefono del referente",
                            fieldName: "referentPhone",
                            defaultValue: referentPhone
                        })}
                        <AddTableRow name={"Posizione"}>
                            <AddressInput
                                address={addressInput}
                                setAddress={setAddressInput}
                                lat={lat}
                                setLat={setLat}
                                lng={lng}
                                setLng={setLng}
                            />
                        </AddTableRow>
                        {/*<AddTableRow name={"Posizione"}>*/}
                        {/*</AddTableRow>*/}
                        <AddTableRow name={"Immagine"}>
                            <ImageSelectorComponent
                                setImageModel={setMainImage}
                                imageModel={mainImage}
                                handleCloseButtonClick={(model) => {
                                    if (id) setImageToDelete([...(imageToDelete || []), model?.id || ""]);
                                    setIsDelete(true)
                                }}
                                isMain={true}/>
                        </AddTableRow>

                        <AddTableRow name={"Immagini"}>
                            <MediaSelectorComponent setImages={setImages} images={images}
                                                    handleCloseButtonClick={(value) => {
                                                        if (id) setImageToDelete([...(imageToDelete || []), value?.id || ""]);
                                                    }}
                                                    description={"Seleziona le foto, i video o gli audio che vorresti utilizzare per descrivere al meglio il produttore."}
                            />
                        </AddTableRow>
                    </Tbody>
                </Table>
            </TableContainer>
        </VStack>
    </BaseContentPage>
}
